import { useState, useEffect } from "react";

export default function PWAInstallBanner() {
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const ua = window.navigator.userAgent;
    const iosDevice = /iphone|ipad|ipod/i.test(ua);
    const androidDevice = /android/i.test(ua);
    const isSafari = /safari/i.test(ua) && !/crios/i.test(ua) && !/fxios/i.test(ua) && !/chrome/i.test(ua);
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;

    // If currently running as installed PWA — mark as installed, hide banner
    if (isStandalone) {
      localStorage.setItem("pwa-installed", "true");
      return;
    }

    // Not standalone — check if they previously installed (meaning they uninstalled)
    const wasInstalled = localStorage.getItem("pwa-installed") === "true";

    // Capture Android prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    if (iosDevice && isSafari) {
      setIsIOS(true);
      setTimeout(() => setShow(true), 3000);
    } else if (androidDevice) {
      if (wasInstalled) {
        // They uninstalled — force show banner immediately, reset flag
        localStorage.removeItem("pwa-installed");
        setTimeout(() => setShow(true), 1500);
      } else {
        // Normal first visit — show after 3s
        setTimeout(() => setShow(true), 3000);
      }
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        localStorage.setItem("pwa-installed", "true");
        setShow(false);
      }
      setDeferredPrompt(null);
    }
  };

  if (!show) return null;

  return (
    <>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(24px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes bounceDown {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(5px); }
        }
        .pwa-banner {
          position: fixed; bottom: 1.25rem; left: 50%;
          transform: translateX(-50%);
          width: calc(100% - 2rem); max-width: 400px;
          z-index: 9999; animation: slideUp 0.35s ease-out;
        }
        .pwa-card {
          background: linear-gradient(135deg, #1c0035 0%, #0e001e 100%);
          border: 1px solid rgba(168,85,247,0.4); border-radius: 1.25rem;
          padding: 1rem 1.125rem;
          box-shadow: 0 8px 32px rgba(168,85,247,0.2), 0 2px 12px rgba(0,0,0,0.5);
        }
        .pwa-row { display: flex; align-items: center; gap: 0.75rem; }
        .pwa-logo { width: 46px; height: 46px; border-radius: 0.875rem; flex-shrink: 0; object-fit: cover; }
        .pwa-text { flex: 1; min-width: 0; }
        .pwa-title {
          color: #fff; font-weight: 700; font-size: 0.9rem; margin: 0 0 4px;
          font-family: 'Cabinet Grotesk', 'IBM Plex Sans', sans-serif;
        }
        .pwa-steps { color: rgba(216,180,254,0.85); font-size: 0.78rem; line-height: 1.6; margin: 0; }
        .pwa-steps strong { color: #e9d5ff; }
        .pwa-share-icon {
          display: inline-flex; align-items: center; vertical-align: middle;
          margin: 0 2px; background: rgba(168,85,247,0.2);
          border-radius: 5px; padding: 1px 4px;
        }
        .pwa-divider { height: 1px; background: rgba(168,85,247,0.2); margin: 0.75rem 0 0.5rem; }
        .pwa-footer { display: flex; align-items: center; justify-content: space-between; }
        .pwa-hint { color: rgba(192,132,252,0.7); font-size: 0.72rem; font-style: italic; }
        .pwa-close {
          background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12);
          border-radius: 9999px; color: rgba(255,255,255,0.55);
          width: 28px; height: 28px; display: flex; align-items: center;
          justify-content: center; cursor: pointer; font-size: 1.1rem; flex-shrink: 0;
        }
        .pwa-install-btn {
          background: linear-gradient(90deg, #c026d3, #7c3aed); color: #fff;
          border: none; border-radius: 9999px; padding: 0.45rem 1.1rem;
          font-size: 0.82rem; font-weight: 600; cursor: pointer; white-space: nowrap;
        }
        .pwa-arrow { text-align: center; margin-top: 0.4rem; animation: bounceDown 1.4s ease-in-out infinite; }
      `}</style>

      <div className="pwa-banner">
        <div className="pwa-card">
          <div className="pwa-row">
            <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="EarnMate" className="pwa-logo" />
            <div className="pwa-text">
              <p className="pwa-title">Install EarnMate App</p>
              {isIOS ? (
                <p className="pwa-steps">
                  1. Tap <span className="pwa-share-icon"><ShareIcon /></span> <strong>Share</strong> at the bottom<br />
                  2. Scroll down → tap <strong>"Add to Home Screen"</strong><br />
                  3. Tap <strong>"Add"</strong> — done!
                </p>
              ) : (
                <p className="pwa-steps">
                  Add EarnMate to your home screen for a faster, app-like experience
                </p>
              )}
            </div>
            <button className="pwa-close" onClick={() => setShow(false)} aria-label="Dismiss">×</button>
          </div>

          {!isIOS && (
            <>
              <div className="pwa-divider" />
              <div className="pwa-footer">
                <span className="pwa-hint">Free · No App Store needed</span>
                <button className="pwa-install-btn" onClick={handleInstall}>
                  {deferredPrompt ? "Install Now" : "How to Install"}
                </button>
              </div>
            </>
          )}
        </div>

        {isIOS && (
          <div className="pwa-arrow">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="rgba(168,85,247,0.8)" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <polyline points="19 12 12 19 5 12"/>
            </svg>
          </div>
        )}
      </div>
    </>
  );
}

function ShareIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="#c084fc" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/>
      <polyline points="16 6 12 2 8 6"/>
      <line x1="12" y1="2" x2="12" y2="15"/>
    </svg>
  );
}